import { Link } from 'react-router-dom';
import Button from '../../components/base/Button';
import { caseStudies, skills } from '../../mocks/portfolio';

export default function Home() {
  const featuredCaseStudies = caseStudies.slice(0, 3);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('https://public.readdy.ai/ai/img_res/506c9ad74275717946c18ecb69f11424.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent">
              Wade Ambrose
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-4">
              Senior Product/UX Designer
            </p>
            <p className="text-lg md:text-xl text-gray-400 mb-8 max-w-3xl mx-auto leading-relaxed">
              Crafting meaningful digital experiences that bridge user needs with business goals. 
              10+ years of experience designing products that millions of people love to use.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/work">
                <Button variant="primary" size="lg">
                  View My Work
                  <i className="ri-arrow-right-line ml-2"></i>
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="secondary" size="lg">
                  Get In Touch
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-2xl text-gray-400"></i>
        </div>
      </section>

      {/* Featured Work Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Featured Work</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              A selection of recent projects that showcase my approach to solving complex design challenges
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {featuredCaseStudies.map((study, index) => (
              <div 
                key={study.id}
                className="group bg-gray-800 rounded-2xl overflow-hidden hover:transform hover:scale-105 transition-all duration-500 cursor-pointer"
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-purple-400 font-medium">{study.category}</span>
                    <span className="text-sm text-gray-500">{study.year}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3 group-hover:text-purple-400 transition-colors duration-300">
                    {study.title}
                  </h3>
                  <p className="text-gray-400 mb-4 line-clamp-3">
                    {study.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {study.tags.slice(0, 2).map((tag) => (
                      <span 
                        key={tag}
                        className="px-3 py-1 bg-gray-700 text-gray-300 text-sm rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/work">
              <Button variant="primary" size="lg">
                View All Case Studies
                <i className="ri-arrow-right-line ml-2"></i>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Design Expertise</h2>
              <p className="text-xl text-gray-400 mb-8">
                With 10+ years of experience, I specialize in creating user-centered designs that drive business success. 
                My approach combines strategic thinking with creative execution.
              </p>
              <Link to="/about">
                <Button variant="primary">
                  Learn More About Me
                  <i className="ri-arrow-right-line ml-2"></i>
                </Button>
              </Link>
            </div>
            
            <div className="space-y-6">
              {skills.slice(0, 6).map((skill) => (
                <div key={skill.name} className="bg-gray-900 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-white font-medium">{skill.name}</span>
                    <span className="text-purple-400 text-sm">
                      {skill.level >= 90 ? 'Expert' : 
                       skill.level >= 85 ? 'Advanced' : 
                       skill.level >= 80 ? 'Proficient' : 'Intermediate'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-900 via-gray-900 to-pink-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Create Something Amazing?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Let&apos;s collaborate on your next project and

 bring your vision to life with exceptional design.
          </p>
          <Link to="/contact">
            <Button variant="primary" size="lg">
              Start a Project
              <i className="ri-arrow-right-line ml-2"></i>
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
